import { Controller } from "@hotwired/stimulus"

// In-browser voice-note recorder for voice-demo feedback (#7039 v2).
//
// MediaRecorder captures mic audio; on stop the blob is wrapped in a File and
// attached to the form's hidden file input via DataTransfer, so the feedback
// form submits as a normal multipart POST — no custom upload path. Degrades
// gracefully: no MediaRecorder support or a denied mic leaves the text
// feedback flow fully working (the status line explains why).
//
// Usage (inside the feedback form):
//   form[data-controller="voice-note-recorder"]
//     button[data-voice-note-recorder-target="recordButton"
//            data-action="voice-note-recorder#toggle"]
//     span[data-voice-note-recorder-target="status"]
//     audio.d-none[data-voice-note-recorder-target="preview"]
//     input[type=file].d-none[data-voice-note-recorder-target="fileInput"]
export default class extends Controller {
  static targets = ["recordButton", "status", "preview", "fileInput"]

  connect() {
    this.recorder = null
    this.chunks = []
  }

  disconnect() {
    this.stopStream()
    this.revokePreviewUrl()
  }

  // Object URLs outlive the controller across Turbo navigations — revoke the
  // previous one before replacing it (and on teardown) so re-recordings don't
  // pin every blob in memory until full page unload.
  revokePreviewUrl() {
    if (this.previewTarget.src?.startsWith("blob:")) {
      URL.revokeObjectURL(this.previewTarget.src)
    }
  }

  toggle(event) {
    event.preventDefault()
    if (this.recording()) {
      this.stop()
    } else {
      this.start()
    }
  }

  async start() {
    if (!this.supported()) {
      this.setStatus("Voice notes aren't supported in this browser — written feedback still works.")
      this.recordButtonTarget.disabled = true
      return
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      this.setStatus("Microphone access was denied — written feedback still works.")
      return
    }

    this.chunks = []
    this.recorder = new MediaRecorder(this.stream)
    this.recorder.addEventListener("dataavailable", (e) => { if (e.data.size > 0) this.chunks.push(e.data) })
    this.recorder.addEventListener("stop", () => this.finalize())
    this.recorder.start()
    this.setRecordingUi(true)
    this.setStatus("Recording… tap stop when you're done.")
  }

  stop() {
    if (this.recording()) this.recorder.stop()
  }

  // Build the file from the captured chunks and hand it to the hidden file
  // input — from here on it's an ordinary attached file.
  finalize() {
    const type = this.recorder?.mimeType || "audio/webm"
    const blob = new Blob(this.chunks, { type })
    const extension = type.split(";")[0].split("/")[1] || "webm"
    const file = new File([blob], `voice-note.${extension}`, { type: blob.type })

    const transfer = new DataTransfer()
    transfer.items.add(file)
    this.fileInputTarget.files = transfer.files
    // Setting `files` fires nothing; announce it like a picked file so the
    // feedback form's card-dirty gate turns Send Feedback on (BUTTON-9).
    this.fileInputTarget.dispatchEvent(new Event("change", { bubbles: true }))

    this.revokePreviewUrl()
    this.previewTarget.src = URL.createObjectURL(blob)
    this.previewTarget.classList.remove("d-none")

    this.stopStream()
    this.setRecordingUi(false)
    this.setStatus("Voice note attached — it uploads when you send your feedback.")
  }

  recording() {
    return this.recorder?.state === "recording"
  }

  supported() {
    return Boolean(navigator.mediaDevices?.getUserMedia && window.MediaRecorder)
  }

  stopStream() {
    this.stream?.getTracks()?.forEach((track) => track.stop())
    this.stream = null
  }

  setRecordingUi(recording) {
    const icon = recording ? "ph-stop-circle" : "ph-microphone"
    const label = recording ? "Stop recording" : "Re-record voice note"
    const initial = !recording && this.fileInputTarget.files.length === 0

    const iconEl = document.createElement("i")
    iconEl.className = `ph ${icon} me-1`
    this.recordButtonTarget.replaceChildren(
      iconEl,
      document.createTextNode(initial ? "Record a voice note" : label)
    )
    this.recordButtonTarget.classList.toggle("gl-voice-recording", recording)
  }

  setStatus(message) {
    this.statusTarget.textContent = message
  }
}
